import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, Plus, X, Pencil, GripVertical } from 'lucide-react';
import { format } from 'date-fns';
import { useSettings } from '@/contexts/SettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SortableSubtaskItem({ subtask, toggleSubtask, removeSubtask, editSubtask, dragHandlePosition }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(subtask.title);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: subtask.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleSaveEdit = () => {
    if (editValue.trim()) {
      editSubtask(subtask.id, editValue.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditValue(subtask.title);
    setIsEditing(false);
  };

  const dragHandle = (
    <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
      <GripVertical size={16} className="text-muted-foreground" />
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-lg bg-muted min-h-[40px]"
      data-testid="edit-subtask-item"
    >
      {dragHandlePosition === 'left' && dragHandle}
      <input
        type="checkbox"
        checked={subtask.completed}
        onChange={() => toggleSubtask(subtask.id)}
        className="h-4 w-4 flex-shrink-0"
      />
      {isEditing ? (
        <>
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveEdit();
              } else if (e.key === 'Escape') {
                handleCancelEdit();
              }
            }}
            className="flex-1 h-6 text-sm py-0 px-2"
            autoFocus
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={handleSaveEdit}
          >
            ✓
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={handleCancelEdit}
          >
            <X size={14} />
          </Button>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${subtask.completed ? 'line-through text-muted-foreground' : ''}`}>
            {subtask.title}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-black"
            onClick={() => setIsEditing(true)}
          >
            <Pencil size={14} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-black"
            onClick={() => removeSubtask(subtask.id)}
          >
            <X size={14} />
          </Button>
        </>
      )}
      {dragHandlePosition === 'right' && dragHandle}
    </div>
  );
}

export default function EditTaskDialog({ open, onOpenChange, task, onTaskUpdated }) {
  const { assigneeTagsEnabled, energyTagsEnabled, dragHandlePosition, contextsEnabled, contexts, activeLists } = useSettings();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [taskContextId, setTaskContextId] = useState('');
  const [taskActiveListId, setTaskActiveListId] = useState('');
  const [assigneeTag, setAssigneeTag] = useState('');
  const [tagType, setTagType] = useState('');
  const [tagDomain, setTagDomain] = useState('');
  const [tagEnergy, setTagEnergy] = useState('');
  const [activeDate, setActiveDate] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tags, setTags] = useState([]);

  // Fetch tags (filtered by context when contexts are enabled)
  useEffect(() => {
    const fetchTags = async () => {
      try {
        // Fetch all tags and filter client-side to include both context-specific and assignee tags
        const tagsRes = await axios.get(`${API}/tags`, { withCredentials: true });
        setTags(tagsRes.data);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  // Populate form when task changes
  useEffect(() => {
    if (task && open) {
      setTitle(task.title || '');
      setNotes(task.notes || '');
      setIsActive(task.is_active || false);
      setTaskContextId(task.context_id || '');
      // Default the active list: existing task.active_list_id, else first list, else ''
      if (activeLists && activeLists.length > 0) {
        const existing = task.active_list_id && activeLists.find(l => l.id === task.active_list_id);
        setTaskActiveListId(existing ? task.active_list_id : activeLists[0].id);
      } else {
        setTaskActiveListId('');
      }
      setAssigneeTag(task.assignee_tag || '');
      setTagType(task.tag_type || '');
      setTagDomain(task.tag_domain || '');
      setTagEnergy(task.tag_energy || '');
      // Parse dates in local timezone to avoid timezone shift
      if (task.activation_date) {
        // Handle both ISO timestamp and YYYY-MM-DD formats
        if (task.activation_date.includes('T')) {
          const date = new Date(task.activation_date);
          setActiveDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
        } else {
          const [year, month, day] = task.activation_date.split('-').map(Number);
          setActiveDate(new Date(year, month - 1, day));
        }
      } else {
        setActiveDate(null);
      }
      if (task.due_date) {
        // Handle both ISO timestamp and YYYY-MM-DD formats
        if (task.due_date.includes('T')) {
          const date = new Date(task.due_date);
          setDueDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
        } else {
          const [year, month, day] = task.due_date.split('-').map(Number);
          setDueDate(new Date(year, month - 1, day));
        }
      } else {
        setDueDate(null);
      }
      setSubtasks(task.subtasks || []);
    }
  }, [task, open]);

  // Keyboard shortcuts: Esc to close, Option+Enter (Mac) / Ctrl+Enter (Windows) to save
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e) => {
      // Option+Enter (Mac) or Ctrl+Enter (Windows) to save
      const isSaveShortcut = (e.altKey || e.ctrlKey) && e.key === 'Enter';
      if (isSaveShortcut && !submitting) {
        e.preventDefault();
        // Submit the form
        const form = document.querySelector('[data-testid="edit-task-dialog"] form');
        if (form) {
          form.requestSubmit();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, submitting]);

  // Filter tags by task's context when contexts are enabled
  // Assignee tags are global (no context filtering)
  const contextFilteredTags = contextsEnabled && taskContextId
    ? tags.filter(t => t.category === 'assignee' || t.context_id === taskContextId)
    : tags;

  const typeTags = contextFilteredTags.filter((t) => t.category === 'Type').sort((a, b) => a.priority - b.priority);
  const domainTags = contextFilteredTags.filter((t) => t.category === 'Domain').sort((a, b) => a.priority - b.priority);
  const energyTags = contextFilteredTags.filter((t) => t.category === 'Energy').sort((a, b) => a.priority - b.priority);
  // Assignee tags sorted by priority
  const assigneeTags = contextFilteredTags
    .filter((t) => t.category === 'assignee')
    .sort((a, b) => a.priority - b.priority);

  // Reset tag selections when context changes (they might not be valid in new context)
  useEffect(() => {
    if (contextsEnabled && taskContextId && task) {
      // Only reset if the context changed from the original task context
      if (taskContextId !== task.context_id) {
        // Check if current selections are still valid for the new context
        const validType = typeTags.find(t => `${t.emoji} ${t.name}` === tagType);
        const validDomain = domainTags.find(t => `${t.emoji} ${t.name}` === tagDomain);
        const validEnergy = energyTags.find(t => `${t.emoji} ${t.name}` === tagEnergy);
        
        if (!validType && tagType) setTagType('');
        if (!validDomain && tagDomain) setTagDomain('');
        if (!validEnergy && tagEnergy) setTagEnergy('');
      }
    }
  }, [taskContextId, contextsEnabled]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSubtasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const editSubtask = (id, newTitle) => {
    setSubtasks(subtasks.map((st) => (st.id === id ? { ...st, title: newTitle } : st)));
  };

  const addSubtask = () => {
    if (newSubtask.trim()) {
      setSubtasks([...subtasks, { id: Date.now().toString(), title: newSubtask, completed: false }]);
      setNewSubtask('');
    }
  };

  const removeSubtask = (id) => {
    setSubtasks(subtasks.filter((s) => s.id !== id));
  };

  const toggleSubtask = (id) => {
    setSubtasks(subtasks.map((s) => 
      s.id === id ? { ...s, completed: !s.completed } : s
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    if (assigneeTagsEnabled && !assigneeTag) {
      toast.error('Please select an assignee');
      return;
    }

    if (!tagType || !tagDomain || (energyTagsEnabled && !tagEnergy)) {
      toast.error(energyTagsEnabled 
        ? 'Please select all tag categories (Type, Domain, Energy)' 
        : 'Please select all tag categories (Type, Domain)');
      return;
    }

    setSubmitting(true);

    try {
      await axios.patch(`${API}/tasks/${task.id}`, {
        title: title.trim(),
        notes: notes.trim() || null,
        is_active: isActive,
        assigned_to_today: false,
        assignee_tag: assigneeTag || null,
        tag_type: tagType,
        tag_domain: tagDomain,
        tag_energy: energyTagsEnabled ? tagEnergy : (tagEnergy || task.tag_energy || null),
        activation_date: activeDate ? format(activeDate, 'yyyy-MM-dd') : null,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
        subtasks,
        context_id: contextsEnabled ? taskContextId : null,
        active_list_id: taskActiveListId || null,
      }, { withCredentials: true });

      toast.success('Task updated successfully!', { duration: 3000 });
      onOpenChange(false);
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto edit-task-dialog"
        data-testid="edit-task-dialog"
        hideCloseButtonOnMobile
      >
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="h-font text-xl">Edit Task</DialogTitle>
          {/* Top buttons - mobile only */}
          <div className="flex gap-3 md:hidden">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-button-top"
            >
              Cancel
            </Button>
            <Button 
              type="button"
              disabled={submitting} 
              onClick={handleSubmit}
              data-testid="save-changes-button-top"
              className="text-white hover:text-black"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Title, Assignee, Active row */}
          {/* When assignee enabled: 2 equal columns on desktop, stacked on mobile */}
          {/* When assignee disabled: Title expands, Active stays small on right (both mobile & desktop) */}
          <div className={`grid gap-4 ${assigneeTagsEnabled ? 'grid-cols-1 md:grid-cols-2 md:items-end' : 'grid-cols-[1fr_auto] items-end'}`}>
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter task title"
                data-testid="edit-task-title-input"
              />
            </div>

            {/* Second column: Assignee + Active toggle side by side, aligned to bottom */}
            <div className="flex gap-4 items-end">
              {/* Assignee - fills available space when enabled */}
              {assigneeTagsEnabled && (
                <div className="space-y-2 flex-1">
                  <Label>Assignee *</Label>
                  <Select value={assigneeTag} onValueChange={setAssigneeTag}>
                    <SelectTrigger data-testid="edit-assignee-select">
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeTags.map((tag) => (
                        <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                          {tag.emoji} {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Active toggle - always small width */}
              <div className="space-y-2">
                <Label htmlFor="edit-is-active">Active?</Label>
                <div className="flex items-center h-9 px-3 rounded-md bg-background border border-input shadow-sm">
                  <Switch
                    id="edit-is-active"
                    checked={isActive}
                    onCheckedChange={setIsActive}
                    data-testid="edit-is-active-switch"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Context/Type/Domain/Energy row - all on same row on desktop */}
          {/* Grid columns: 4 if both context+energy, 3 if one disabled, 2 if both disabled */}
          <div className={`grid grid-cols-1 gap-4 ${
            contextsEnabled && energyTagsEnabled ? 'md:grid-cols-4' :
            contextsEnabled || energyTagsEnabled ? 'md:grid-cols-3' :
            'md:grid-cols-2'
          }`}>
            {contextsEnabled && contexts.length > 0 && (
              <div className="space-y-2">
                <Label>Context</Label>
                <Select
                  value={taskContextId}
                  onValueChange={(val) => {
                    setTaskContextId(val);
                    const ctx = contexts.find((c) => c.id === val);
                    if (ctx?.active_list_id) {
                      setTaskActiveListId(ctx.active_list_id);
                    }
                  }}
                >
                  <SelectTrigger data-testid="edit-task-context-select">
                    <SelectValue placeholder="Select context" />
                  </SelectTrigger>
                  <SelectContent>
                    {contexts.map((context) => (
                      <SelectItem key={context.id} value={context.id}>
                        {context.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={tagType} onValueChange={setTagType}>
                <SelectTrigger data-testid="edit-tag-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {typeTags.map((tag) => (
                    <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                      {tag.emoji} {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Domain *</Label>
              <Select
                value={tagDomain}
                onValueChange={(val) => {
                  setTagDomain(val);
                  const tag = domainTags.find((t) => `${t.emoji} ${t.name}` === val);
                  if (tag?.active_list_id) {
                    setTaskActiveListId(tag.active_list_id);
                  }
                }}
              >
                <SelectTrigger data-testid="edit-tag-domain-select">
                  <SelectValue placeholder="Select domain" />
                </SelectTrigger>
                <SelectContent>
                  {domainTags.map((tag) => (
                    <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                      {tag.emoji} {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {energyTagsEnabled && (
              <div className="space-y-2">
                <Label>Energy *</Label>
                <Select value={tagEnergy} onValueChange={setTagEnergy}>
                  <SelectTrigger data-testid="edit-tag-energy-select">
                    <SelectValue placeholder="Select energy" />
                  </SelectTrigger>
                  <SelectContent>
                    {energyTags.map((tag) => (
                      <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                        {tag.emoji} {tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className={`grid grid-cols-1 gap-4 ${activeLists && activeLists.length > 1 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {activeLists && activeLists.length > 1 && (
              <div className="space-y-2">
                <Label>Active List</Label>
                <Select value={taskActiveListId} onValueChange={setTaskActiveListId}>
                  <SelectTrigger data-testid="edit-task-active-list-select">
                    <SelectValue placeholder="Select list" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Active Date</Label>
              {/* Native date input on mobile, custom picker on desktop */}
              <div className="md:hidden flex gap-2">
                <Input
                  type="date"
                  value={activeDate ? format(activeDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Parse date in local timezone
                      const [year, month, day] = e.target.value.split('-');
                      setActiveDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
                    } else {
                      setActiveDate(null);
                    }
                  }}
                  className="flex-1"
                  data-testid="edit-active-date-input-mobile"
                />
                {activeDate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setActiveDate(null)}
                    className="shrink-0"
                  >
                    ×
                  </Button>
                )}
              </div>
              <div className="hidden md:block relative">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${activeDate ? 'pr-9' : ''}`}
                      data-testid="edit-active-picker-trigger"
                    >
                      {!activeDate && <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />}
                      <span className="truncate">{activeDate ? format(activeDate, 'PPP') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={activeDate}
                      onSelect={setActiveDate}
                      initialFocus
                      data-testid="edit-active-calendar"
                    />
                  </PopoverContent>
                </Popover>
                {activeDate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setActiveDate(null)}
                    className="absolute right-0 top-0 h-9 w-9"
                    data-testid="edit-active-date-clear-button"
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              {/* Native date input on mobile, custom picker on desktop */}
              <div className="md:hidden flex gap-2">
                <Input
                  type="date"
                  value={dueDate ? format(dueDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Parse date in local timezone
                      const [year, month, day] = e.target.value.split('-');
                      setDueDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
                    } else {
                      setDueDate(null);
                    }
                  }}
                  className="flex-1"
                  data-testid="edit-due-date-input-mobile"
                />
                {dueDate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setDueDate(null)}
                    className="shrink-0"
                  >
                    ×
                  </Button>
                )}
              </div>
              <div className="hidden md:block relative">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${dueDate ? 'pr-9' : ''}`}
                      data-testid="edit-due-picker-trigger"
                    >
                      {!dueDate && <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />}
                      <span className="truncate">{dueDate ? format(dueDate, 'PPP') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                      data-testid="edit-due-calendar"
                    />
                  </PopoverContent>
                </Popover>
                {dueDate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setDueDate(null)}
                    className="absolute right-0 top-0 h-9 w-9"
                    data-testid="edit-due-date-clear-button"
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Subtasks</Label>
            {subtasks.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={subtasks.map((st) => st.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {subtasks.map((subtask) => (
                      <SortableSubtaskItem
                        key={subtask.id}
                        subtask={subtask}
                        toggleSubtask={toggleSubtask}
                        removeSubtask={removeSubtask}
                        editSubtask={editSubtask}
                        dragHandlePosition={dragHandlePosition}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            <div className="flex gap-2">
              <Input
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtask())}
                placeholder="Add a subtask"
                data-testid="edit-subtask-input"
              />
              <Button
                type="button"
                onClick={addSubtask}
                size="icon"
                variant="secondary"
                data-testid="edit-add-subtask-button"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes"
              rows={3}
              data-testid="edit-task-notes-input"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="edit-cancel-button"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting} 
              data-testid="edit-save-button"
              style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
